import { useMemo, useState } from 'react';

import { SortDirection, SortDirectionType } from '@/api/types';
import { Badge, getTableMinWidth, Table, TableColumn, TableRow, TextCell } from '@/design-system';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { EmptyState, MobileCardField, MobileFieldsGrid } from '@/shared/design-system';
import { formatDefaultFractionTwo, formatWithoutFraction } from '@/utils/numbers/formatters';

import { MobileCardList } from './my-assets/mobile-card-list';

export type OwnershipInEntitiesRow = {
  id: string;
  entityName: string;
  units: number;
  percentOfTotal: number;
  shareClass: string;
};

const SHARE_CLASS_LABELS: Record<string, string> = {
  ClassA: 'Class A Shares',
  Preferred: 'Preferred Shares',
  Ordinary: 'Ordinary Shares',
  ClassB: 'Class B Shares',
  ClassC: 'Class C Shares',
  LimitedPartnershipUnits: 'Limited Partnership Units',
  GeneralPartnerUnits: 'General Partner Units',
};

const formatPercent = (value?: number) => {
  if (value === null || value === undefined) return '-';
  const pct = value <= 1 ? value * 100 : value;
  return `${formatDefaultFractionTwo(pct)}%`;
};

const formatShareClass = (value?: string) => {
  if (!value) return '-';
  return SHARE_CLASS_LABELS[value] ?? value;
};

const SORT_KEYS = ['entityName', 'units', 'percentOfTotal', 'shareClass'] as const;
type SortKey = (typeof SORT_KEYS)[number];

interface OwnershipTableRow extends TableRow {
  row: OwnershipInEntitiesRow;
}

const compareBy = (key: SortKey, a: OwnershipInEntitiesRow, b: OwnershipInEntitiesRow): number => {
  switch (key) {
    case 'entityName':
      return String(a.entityName ?? '').localeCompare(String(b.entityName ?? ''));
    case 'shareClass':
      return formatShareClass(a.shareClass).localeCompare(formatShareClass(b.shareClass));
    case 'units':
      return (a.units ?? 0) - (b.units ?? 0);
    case 'percentOfTotal':
      return (a.percentOfTotal ?? 0) - (b.percentOfTotal ?? 0);
  }
};

const OwnershipInEntitiesTable = ({ rows }: { rows: OwnershipInEntitiesRow[] }) => {
  const isMobile = useIsMobile();
  const [sortKey, setSortKey] = useState<SortKey>('entityName');
  const [sortDirection, setSortDirection] = useState<SortDirectionType>(SortDirection.ASC);

  const handleSort = (key: string) => {
    if (!SORT_KEYS.includes(key as SortKey)) return;
    if (key === sortKey) {
      setSortDirection(
        sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC,
      );
      return;
    }
    setSortKey(key as SortKey);
    setSortDirection(SortDirection.ASC);
  };

  const sortedRows = useMemo(() => {
    const sign = sortDirection === SortDirection.ASC ? 1 : -1;
    return [...rows].sort((a, b) => sign * compareBy(sortKey, a, b));
  }, [rows, sortKey, sortDirection]);

  if (isMobile) {
    return (
      <MobileCardList
        items={sortedRows.map((row) => ({
          value: row.id,
          title: row.entityName,
          badge: (
            <Badge label={formatShareClass(row.shareClass)} color="secondary" variant="subtle" />
          ),
          content: (
            <MobileFieldsGrid>
              <MobileCardField label="# Shares" value={formatWithoutFraction(row.units)} />
              <MobileCardField label="% of Total" value={formatPercent(row.percentOfTotal)} />
            </MobileFieldsGrid>
          ),
        }))}
        defaultValue={sortedRows[0]?.id ?? null}
        emptyMessage="No ownership records."
      />
    );
  }

  if (sortedRows.length === 0) {
    return <EmptyState variant="tertiary" title="No ownership records." />;
  }

  const columns: TableColumn<OwnershipTableRow>[] = [
    {
      key: 'entityName',
      header: 'Entity Name',
      sortable: true,
      minWidth: 272,
      cell: ({ row }) => <TextCell title={row.entityName} tooltip={row.entityName} />,
    },
    {
      key: 'units',
      header: '# Shares',
      sortable: true,
      width: '272px',
      cell: ({ row }) => <TextCell title={formatWithoutFraction(row.units)} />,
    },
    {
      key: 'percentOfTotal',
      header: '% of Total',
      sortable: true,
      width: '272px',
      cell: ({ row }) => <TextCell title={formatPercent(row.percentOfTotal)} />,
    },
    {
      key: 'shareClass',
      header: 'Share Class',
      sortable: true,
      width: '296px',
      cell: ({ row }) => (
        <Badge label={formatShareClass(row.shareClass)} color="secondary" variant="subtle" />
      ),
    },
  ];

  const tableRows: OwnershipTableRow[] = sortedRows.map((row) => ({ key: row.id, row }));

  return (
    <Table
      columns={columns}
      rows={tableRows}
      size="lg"
      sortKey={sortKey}
      sortDirection={sortDirection}
      onSort={handleSort}
      minTableWidth={getTableMinWidth(columns)}
    />
  );
};

export default OwnershipInEntitiesTable;
